import axios, { AxiosInstance } from 'axios';
import { ApiResponse, Movie, User, WatchParty, Notification, SubscriptionStatus, PaginatedResponse } from '../types';

// Get base URL - force HTTPS on production
let BASE_URL = window.location.origin;
if (window.location.hostname === 'cineclubs.com' && window.location.protocol === 'http:') {
  window.location.href = window.location.href.replace('http://', 'https://');
}
if (window.location.hostname === 'cineclubs.com') {
  BASE_URL = 'https://cineclubs.com';
}

const API_URL = `${BASE_URL}/api`;

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Fix trailing slashes
  if (config.url) {
    config.url = config.url.replace(/\/+\?/g, '?').replace(/\/+(?=[?#]|$)/g, '');
  }
  
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const authErrors = [
      'Unauthorized - Invalid or expired token',
      'Unauthorized - Please login',
      'Token expired',
      'Invalid token'
    ];
    
    const errorMessage = error.response?.data?.message || error.message;
    const isAuthError = authErrors.some(msg => 
      errorMessage?.toLowerCase().includes(msg.toLowerCase())
    );
    
    if (isAuthError) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
    
    return Promise.reject(error);
  }
);

// Generic API call function
export const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {})
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include'
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || `API Error: ${response.status}`);
  }
  
  return data;
};

// Auth functions
export const authService = {
  login: async (email: string, password: string): Promise<User> => {
    const response = await api.post<ApiResponse<{ token: string; user: User }>>('/auth/login', { email, password });
    if (response.data.status === 'success' && response.data.data.token) {
      localStorage.setItem('auth_token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
      return response.data.data.user;
    }
    throw new Error(response.data.message || 'Login failed');
  },

  register: async (username: string, email: string, password: string, referralCode?: string): Promise<User> => {
    const body: any = { username, email, password };
    if (referralCode?.trim()) {
      body.referral_code = referralCode.trim();
    }
    const response = await api.post<ApiResponse<{ token: string; user: User }>>('/auth/register', body);
    if (response.data.status === 'success' && response.data.data.token) {
      localStorage.setItem('auth_token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
      return response.data.data.user;
    }
    throw new Error(response.data.message || 'Registration failed');
  },

  logout: async (): Promise<void> => {
    try {
      await fetch('/api/logout.php', { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.warn('Server logout failed:', e);
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  },

  getCurrentUser: async (): Promise<User | null> => {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    
    try {
      const response = await api.get<ApiResponse<User>>('/auth/me');
      if (response.data.status === 'success') {
        localStorage.setItem('user', JSON.stringify(response.data.data));
        return response.data.data;
      }
      return null;
    } catch (error) {
      // Return cached user on network error if token still exists
      const tokenStillExists = !!localStorage.getItem('auth_token');
      if (tokenStillExists) {
        try {
          const cachedUser = localStorage.getItem('user');
          if (cachedUser) return JSON.parse(cachedUser);
        } catch (e) {}
      }
      return null;
    }
  },

  getSubscriptionStatus: async (): Promise<SubscriptionStatus | null> => {
    try {
      const response = await api.get<ApiResponse<SubscriptionStatus>>('/payment/subscription-status');
      return response.data.data;
    } catch (error) {
      return null;
    }
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('auth_token');
  },

  getToken: (): string | null => {
    return localStorage.getItem('auth_token');
  }
};

// Movies functions
export const movieService = {
  getMovies: async (page: number = 1, filters: Record<string, any> = {}): Promise<Movie[]> => {
    const params = new URLSearchParams({ page: String(page), per_page: '50', ...filters });
    const response = await api.get<ApiResponse<PaginatedResponse<Movie> | Movie[]>>(`/movies?${params}`);
    
    if (response.data.data) {
      if ('data' in response.data.data && Array.isArray(response.data.data.data)) {
        return response.data.data.data;
      }
      if (Array.isArray(response.data.data)) {
        return response.data.data;
      }
    }
    return [];
  },

  getMovie: async (id: number): Promise<Movie | null> => {
    try {
      const response = await api.get<ApiResponse<Movie>>(`/movies/${id}`);
      return response.data.data;
    } catch (error) {
      return null;
    }
  },

  searchMovies: async (query: string): Promise<Movie[]> => {
    const response = await api.get<ApiResponse<PaginatedResponse<Movie> | Movie[]>>(`/movies?search=${encodeURIComponent(query)}`);
    
    if (response.data.status === 'success') {
      if (response.data.data && 'data' in response.data.data) {
        return response.data.data.data;
      }
      if (Array.isArray(response.data.data)) {
        return response.data.data;
      }
    }
    return [];
  }
};

// Watch Party functions
export const watchPartyService = {
  getActiveParties: async (page: number = 1): Promise<WatchParty[]> => {
    try {
      const response = await fetch(`${BASE_URL}/get-parties-direct.php?page=${page}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      if (!response.ok) return [];
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      return [];
    }
  },

  createParty: async (movieId: number, title: string, maxParticipants: number = 10, password?: string): Promise<WatchParty> => {
    const requestData: any = { title, max_participants: maxParticipants };
    if (movieId && !isNaN(movieId)) {
      requestData.movie_id = movieId;
    }
    if (password?.trim()) {
      requestData.password = password.trim();
    }
    
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/create-party-direct.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create party: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data;
  },

  joinParty: async (partyId: number): Promise<any> => {
    const response = await api.post(`/watch-parties/${partyId}/join`);
    return response.data.data;
  },

  leaveParty: async (partyId: number): Promise<any> => {
    const response = await api.post(`/watch-parties/${partyId}/leave`);
    return response.data.data;
  }
};

// Notifications
export const notificationService = {
  getNotifications: async (): Promise<{ notifications: Notification[]; unread_count: number }> => {
    try {
      const response = await api.get<ApiResponse<{ notifications: Notification[]; unread_count: number }>>('/notifications');
      return response.data.data;
    } catch (error) {
      return { notifications: [], unread_count: 0 };
    }
  },

  getUnreadCount: async (): Promise<number> => {
    try {
      const response = await api.get<ApiResponse<{ unread_count: number }>>('/notifications/unread-count');
      return response.data.data.unread_count;
    } catch (error) {
      return 0;
    }
  },

  markAsRead: async (notificationId: number): Promise<void> => {
    await api.post('/notifications/mark-read', { notification_id: notificationId });
  },

  markAllAsRead: async (): Promise<void> => {
    await api.post('/notifications/mark-all-read');
  }
};

export default api;
